"use client";

import { useEffect, useId, useRef, useState } from "react";
import { LocalContactForm } from "@/components/contact/LocalContactForm.client";
import { getLocalContactContext } from "@/lib/localLessons/contactContext";
import { getLocalLessonProduct, isLocalLessonProductKey } from "@/lib/localLessons/localLessonProductCatalog";

export type LocalLessonTimeHold = {
  flowId: string;
  accessToken: string;
  selectedStart: string;
  selectedEnd: string;
  expiresAt: string;
  checkoutStartDeadline?: string;
};

export function getCheckoutStartDeadline(hold: LocalLessonTimeHold): number {
  // Leave the server’s 35-minute Stripe window and 3-minute provider safety margin.
  const latestStart = Date.parse(hold.expiresAt) - 38 * 60000;
  return hold.checkoutStartDeadline ? Math.min(Date.parse(hold.checkoutStartDeadline), latestStart) : latestStart;
}

export async function calendarFirstRequest(body: Record<string, unknown>) {
  const response = await fetch("/api/local-lessons/calendar-first", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.error || "Live booking is temporarily unavailable. Please try again.");
  return data;
}

export function formatLocalLessonTime(value: string) {
  return new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function shiftDate(date: string, days: number) {
  const day = new Date(`${date}T12:00:00Z`); day.setUTCDate(day.getUTCDate() + days);
  return day.toISOString().slice(0, 10);
}
export function londonMidnight(date: string) {
  const midnight = new Date(`${date}T00:00:00Z`);
  const offset = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", timeZoneName: "shortOffset" }).formatToParts(midnight).find((part) => part.type === "timeZoneName")?.value;
  return new Date(midnight.getTime() - (offset === "GMT+1" ? 3600000 : 0)).toISOString();
}

export function nearestLocalLessonSlots(starts: string[], date: string, now = Date.now()) {
  const dayStart = Date.parse(londonMidnight(date));
  const dayEnd = Date.parse(londonMidnight(shiftDate(date, 1)));
  const rangeStart = Date.parse(londonMidnight(shiftDate(date, -15)));
  const rangeEnd = Date.parse(londonMidnight(shiftDate(date, 16)));
  const eligible = [...new Set(starts)].filter((start) => {
    const time = Date.parse(start);
    return Number.isFinite(time) && time > now && time >= rangeStart && time < rangeEnd;
  }).sort((a, b) => Date.parse(a) - Date.parse(b));
  return {
    before: eligible.filter((start) => Date.parse(start) < dayStart).at(-1),
    after: eligible.find((start) => Date.parse(start) >= dayEnd),
  };
}

function londonDate(value: string) {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(value));
  return ["year", "month", "day"].map((type) => parts.find((part) => part.type === type)?.value).join("-");
}

export function LocalLessonTimeHoldStep({ productKey, venueId, customer, isPack, onHeld }: {
  productKey: string; venueId: string;
  customer: { name: string; email: string; phone: string };
  sourcePage: string; sourceCta: string; isPack: boolean;
  onHeld: (hold: LocalLessonTimeHold) => void;
}) {
  const enquiryId = useId();
  const enquiryButton = useRef<HTMLButtonElement>(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryStarted, setEnquiryStarted] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [date, setDate] = useState(() => {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
    return ["year", "month", "day"].map((type) => parts.find((part) => part.type === type)?.value).join("-");
  });
  const [nearby, setNearby] = useState<{ before?: string; after?: string }>({});
  const [slots, setSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [holding, setHolding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let current = true;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(`${date}T00:00:00Z`))) {
      setLoading(false); setSlots([]); setNearby({}); setError("Please choose a lesson date to see available times.");
      return;
    }
    setLoading(true); setSlots([]); setNearby({}); setError(null);
    calendarFirstRequest({ action: "slots", productKey, venueId, start: londonMidnight(date), end: londonMidnight(shiftDate(date, 1)), timeZone: "Europe/London" })
      .then(async (data) => {
        if (!current) return;
        const starts = data.slots.map((slot: { start: string }) => slot.start) as string[];
        setSlots(starts);
        if (starts.length) return;
        // One bounded range covers both sides, including London clock changes.
        // The server applies the event's minimum notice, buffers and shared calendar conflicts.
        const alternatives = await calendarFirstRequest({ action: "slots", productKey, venueId,
          start: new Date(Math.max(Date.now(), Date.parse(londonMidnight(shiftDate(date, -15))))).toISOString(), end: londonMidnight(shiftDate(date, 16)), timeZone: "Europe/London" });
        if (current) setNearby(nearestLocalLessonSlots(alternatives.slots.map((slot: { start: string }) => slot.start), date));
      })
      .catch((cause) => { if (current) setError(cause.message); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [productKey, venueId, date, revision]);

  async function hold(slotStart: string) {
    setHolding(true); setError(null);
    try {
      const data = await calendarFirstRequest({ action: "hold", productKey, venueId, start: slotStart, timeZone: "Europe/London",
        customerName: customer.name, customerEmail: customer.email, customerPhone: customer.phone });
      if (!data.flowId || !data.accessToken || ![data.selectedStart, data.selectedEnd, data.expiresAt].every((value) => typeof value === "string" && Number.isFinite(Date.parse(value)))) throw new Error("Your selected time could not be verified. Please refresh availability.");
      if (data.checkoutStartDeadline !== undefined && !Number.isFinite(Date.parse(data.checkoutStartDeadline))) throw new Error("Your payment-start deadline could not be verified. Please choose a time again.");
      if (!mounted.current) { void calendarFirstRequest({ action: "release", flowId: data.flowId, accessToken: data.accessToken }).catch(() => undefined); return; }
      onHeld(data);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "This time could not be held. Please choose again.");
    } finally { setHolding(false); }
  }

  return <div className="space-y-4">
    <p className="text-sm leading-6 text-[#53615c]">{isPack ? "Choose your first appointment; the remaining lessons in your pack are scheduled separately. " : ""}All times are UK time. Selecting a time creates a temporary hold before payment.</p>
    {venueId === "bury_st_edmunds_studio" ? <p className="rounded-[14px] border border-[#d9c78f] bg-[#fff5df] p-3 text-sm leading-6 text-[#53615c]">For Bury, Liuba’s time is held first. The room is checked manually after payment; your lesson remains provisional until written room confirmation.</p> : null}
    <label className="grid gap-2 text-sm font-bold text-[#53615c]">Lesson date<input type="date" value={date} onChange={(event) => setDate(event.target.value)} disabled={holding} className="min-h-12 rounded-[14px] border border-[#d8cab6] bg-white px-3 text-[#13211f]" /></label>
    <div aria-live="polite" aria-busy={loading || holding}>
      {loading ? <p>Loading available times…</p> : slots.length ? <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{slots.map((start) => <button key={start} type="button" disabled={holding} onClick={() => hold(start)} className="min-h-12 rounded-[14px] border border-[#d8cab6] px-3 py-2 text-sm font-bold text-[#123b34] hover:bg-[#fff5df] disabled:opacity-60">{formatLocalLessonTime(start)}</button>)}</div> : !error ? <div className="space-y-3">
        <p className="text-sm text-[#53615c]">No times are available on this date. {nearby.before || nearby.after ? "Here are the closest available times within 15 days either side. Choose a day to view its times." : "No nearby times were found within 15 days either side. Please choose another day."}</p>
        <div className="grid gap-2 sm:grid-cols-2">{([ ["Earlier", nearby.before], ["Later", nearby.after] ] as const).map(([label, start]) => start ? <button key={label} type="button" disabled={holding} onClick={() => setDate(londonDate(start))} className="min-h-12 rounded-[14px] border border-[#d8cab6] px-3 py-2 text-sm font-bold text-[#123b34] hover:bg-[#fff5df] disabled:opacity-60">{label}: {formatLocalLessonTime(start)} — view times</button> : null)}</div>
      </div> : null}
      {holding ? <p className="mt-3">Holding your selected time…</p> : null}
    </div>
    {error ? <p role="alert" className="rounded-[14px] border border-[#e8a7b4] bg-[#fff0f2] p-3 text-sm text-[#9f1239]">{error}</p> : null}
    <button type="button" disabled={loading || holding} onClick={() => setRevision((value) => value + 1)} className="min-h-12 text-sm font-bold text-[#123b34] underline">Refresh availability</button>
    <section className="rounded-[18px] border border-[#d9c78f] bg-[#fff5df] p-4">
      <p className="text-sm leading-6 text-[#53615c]">Can’t see a time that works for you?</p>
      <button ref={enquiryButton} type="button" disabled={holding} aria-expanded={enquiryOpen} aria-controls={enquiryId}
        onClick={() => { setEnquiryStarted(true); setEnquiryOpen(true); }}
        className="mt-3 min-h-12 w-full rounded-[14px] bg-[#123b34] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#205247] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7a5114] disabled:opacity-60">
        Talk to the team about another time
      </button>
      <div id={enquiryId} hidden={!enquiryOpen} className="mt-4 space-y-3">
        {enquiryStarted ? <>
          <button type="button" onClick={() => { setEnquiryOpen(false); enquiryButton.current?.focus(); }} className="min-h-11 text-sm font-bold text-[#123b34] underline underline-offset-4">Back to available times</button>
          <LocalContactForm siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? ""}
            initialContext={getLocalContactContext({ studio: venueId, reason: "Booking question" })}
            bookingEnquiry={{ ...customer, message: `I’d like to arrange ${isLocalLessonProductKey(productKey) ? getLocalLessonProduct(productKey).displayName : "a singing lesson"}${date ? ` on or around ${date}` : ""}, but I can’t find a suitable time in the calendar. My preferred dates or times are: ` }} />
        </> : null}
      </div>
    </section>
  </div>;
}
